import {
  type Diagnostic,
  type RoutineView,
  type ValidationContext,
  type ValidationRule,
} from '../types';

export function createStepRule(opts: {
  id: string;
  stepKinds: string[];
  run: (args: {
    routine: RoutineView;
    ctx: ValidationContext;
    stepId: string;
  }) => Diagnostic[];
}): ValidationRule {
  return {
    id: opts.id,
    appliesTo: 'step',
    stepKinds: opts.stepKinds,
    run: ({ routine, ctx, stepId }) => {
      if (!stepId) return [];

      return opts.run({ routine, ctx, stepId });
    },
  };
}

export function diag(error: {
  code: string;
  message: string;
  stepId: string;
  fieldId?: string;
  severity?: 'error' | 'warning';
  tags?: string[];
  sourceRule: string;
}): Diagnostic {
  return {
    code: error.code,
    message: error.message,
    severity: error.severity ?? 'error',
    stepId: error.stepId,
    fieldId: error.fieldId,
    tags: error.tags,
    sourceRule: error.sourceRule,
  };
}

type ActionRequiredShape = {
  kind: string;
  message?: string;
  fieldId?: string;
  deprecationKind?: string;
};

function readActionRequired(err: unknown): ActionRequiredShape | undefined {
  return (err as { actionRequired?: ActionRequiredShape } | undefined)
    ?.actionRequired;
}

/**
 * Convert any caught error into Diagnostics. Recognises ActionRequiredError
 * shapes (kind, message, fieldId, deprecationKind), bare Error, and unknown.
 * Single source of truth for error→diagnostic conversion across both
 * class-based (CanonicalStepRuleBase) and function-style rules.
 */
export function errorToDiagnostics(
  err: unknown,
  stepId: string,
  sourceRule: string,
): Diagnostic[] {
  const ar = readActionRequired(err);

  if (ar) {
    const tags = ar.deprecationKind
      ? [`deprecationKind:${ar.deprecationKind}`]
      : undefined;

    return [
      diag({
        code: `ui.${ar.kind}`,
        message: ar.message ?? 'Invalid configuration',
        fieldId: ar.fieldId,
        stepId,
        sourceRule,
        severity:
          ar.kind === 'deprecatedConfigurationWarning' ||
          ar.kind === 'missingConfiguration'
            ? 'warning'
            : 'error',
        tags,
      }),
    ];
  }

  if (err instanceof Error) {
    return [
      diag({
        code: 'ui.invalidConfiguration',
        message: err.message,
        stepId,
        sourceRule,
      }),
    ];
  }

  return [
    diag({
      code: 'ui.invalidConfiguration',
      message: `Configuration is not valid (${String(err)})`,
      stepId,
      sourceRule,
    }),
  ];
}

/**
 * Catch an ActionRequiredError thrown by legacy validators (e.g.
 * validateConditional) and convert it to a Diagnostic[]. Returns an empty
 * array for non-AR / non-Error values — this swallow-and-continue semantic
 * is intentional: callers run multiple checks in sequence and don't want one
 * unrelated throw to mask the others.
 *
 * Conversion logic delegates to `errorToDiagnostics` so the format stays
 * in sync with the rule-runner's catch path in `rules/steps/base.ts`.
 */
export function catchActionRequiredToDiagnostic(
  err: unknown,
  stepId: string,
  sourceRule: string,
): Diagnostic[] {
  if (!readActionRequired(err) && !(err instanceof Error)) return [];

  return errorToDiagnostics(err, stepId, sourceRule);
}
